import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Send, Loader2, FileSignature, Copy, Link as LinkIcon, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { emailTemplatesAPI, variationsAPI } from '../lib/api';
import { EmailModeToggle } from './EmailModeToggle';
import { EmailPreviewDialog } from './EmailPreviewDialog';
import { resolveBodyHtml, replaceCustomerVars } from '../lib/emailEditor';

const DEFAULT_SUBJECT = 'Please sign your Variation of Contract — {{first_name}}';
const DEFAULT_BODY = [
  '<p>Hi {{first_name}},</p>',
  '<p>Your <strong>Variation of Contract</strong> is ready to sign. Use the secure link below — no login required.</p>',
  '<p style="margin: 20px 0;"><a href="{{sign_link}}" style="background:#2563eb;color:#fff;padding:12px 22px;border-radius:6px;text-decoration:none;display:inline-block;font-weight:600;">Sign now</a></p>',
  '<p style="color:#475569;font-size:12px;">This link expires on {{expires_on}}.</p>',
  '<p>Kind regards,<br/>Helios Electrical &amp; Solar</p>',
].join('\n');

/**
 * Dialog shown when an admin clicks "Send Sign Link" on a Variation document.
 * Lets them pick an email template (or write inline) before firing the
 * tokenised public link off to the customer.
 */
export const SendSignLinkDialog = ({ open, onOpenChange, customer, document: variationDoc, onSent }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTrigger, setSelectedTrigger] = useState('variation_sign_link');
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [editorMode, setEditorMode] = useState('html');
  const [previewOpen, setPreviewOpen] = useState(false);

  // Load all email templates once on open
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setSendEmail(true);
    setEditorMode('html');
    emailTemplatesAPI.getAll()
      .then((r) => {
        const list = r.data || [];
        setTemplates(list);
        // Default to variation_sign_link if it exists
        const pref = list.find((t) => t.trigger === 'variation_sign_link');
        if (pref) {
          setSelectedTrigger('variation_sign_link');
          setSubject(pref.subject || DEFAULT_SUBJECT);
          setBody(pref.body_html || DEFAULT_BODY);
        } else {
          setSelectedTrigger('variation_sign_link');
          setSubject(DEFAULT_SUBJECT);
          setBody(DEFAULT_BODY);
        }
      })
      .catch(() => setTemplates([]));
  }, [open]);

  const handleTemplateChange = (trigger) => {
    setSelectedTrigger(trigger);
    if (trigger === 'inline') {
      // keep current subject/body so the admin can tweak
      return;
    }
    const t = templates.find((x) => x.trigger === trigger);
    if (t) {
      setSubject(t.subject || DEFAULT_SUBJECT);
      setBody(t.body_html || DEFAULT_BODY);
    }
  };

  const submit = async () => {
    if (sendEmail && !customer?.email) {
      toast.error('This customer has no email address on file.');
      return;
    }
    setBusy(true);
    try {
      const trigger = selectedTrigger === 'inline' ? null : selectedTrigger;
      const r = await variationsAPI.createShareLinkAdvanced(customer.id, variationDoc.id, {
        send_email: sendEmail,
        template_trigger: trigger,
        subject: sendEmail ? subject : null,
        body_html: sendEmail ? resolveBodyHtml(body, editorMode) : null,
      });
      setResult(r.data);
      if (r.data?.email_sent) {
        toast.success(`Sign link emailed to ${customer.email}`);
      } else if (sendEmail) {
        toast.success('Sign link generated — copy + share manually.', { duration: 8000 });
      } else {
        toast.success('Sign link generated.');
      }
      if (onSent) onSent(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create sign link');
    } finally { setBusy(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text || '');
    toast.success('Copied to clipboard');
  };

  // Variation-related triggers float to the top of the dropdown
  const orderedTemplates = [
    ...templates.filter((t) => t.trigger?.startsWith('variation_')),
    ...templates.filter((t) => !t.trigger?.startsWith('variation_')),
  ];

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="send-sign-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSignature className="w-5 h-5 text-blue-600" /> Send Variation Sign Link</DialogTitle>
          <DialogDescription>
            Generate a tokenised one-click sign link for <strong>{customer?.first_name} {customer?.last_name}</strong> and optionally email it using a template.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3" data-testid="sign-link-result">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-900">
              <p className="font-semibold flex items-center gap-1">
                {result.email_sent ? '✓ Email sent successfully' : '✓ Sign link generated'}
              </p>
              <p className="text-xs mt-1">
                Expires on <strong>{new Date(result.expires_at).toLocaleDateString()}</strong>.
                Single-use — once the customer signs the link is invalidated.
              </p>
            </div>
            <div>
              <Label className="text-xs">Public sign link</Label>
              <div className="flex gap-2 mt-1">
                <Input readOnly value={result.public_url} className="font-mono text-xs" data-testid="sign-link-url" />
                <Button variant="outline" onClick={() => copy(result.public_url)} data-testid="sign-link-copy"><Copy className="w-4 h-4" /></Button>
                <Button asChild variant="outline" data-testid="sign-link-open">
                  <a href={result.public_url} target="_blank" rel="noreferrer"><LinkIcon className="w-4 h-4" /></a>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded border-slate-300 text-blue-600"
                data-testid="send-email-toggle"
              />
              <span>
                <span className="font-medium">Email the link to {customer?.email || 'the customer'}</span>
                <br /><span className="text-xs text-slate-500">Untick to just generate the URL and share it manually.</span>
              </span>
            </label>

            {sendEmail && (
              <>
                <div>
                  <Label className="text-xs">Email template</Label>
                  <Select value={selectedTrigger} onValueChange={handleTemplateChange}>
                    <SelectTrigger data-testid="sign-link-template"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inline">— Custom (edit below) —</SelectItem>
                      {orderedTemplates.map((t) => (
                        <SelectItem key={t.trigger} value={t.trigger}>
                          {t.label || t.trigger}{!t.active ? ' (inactive)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Variables: <code className="bg-slate-100 px-1 rounded">{'{{first_name}}'}</code>,{' '}
                    <code className="bg-slate-100 px-1 rounded">{'{{sign_link}}'}</code>,{' '}
                    <code className="bg-slate-100 px-1 rounded">{'{{expires_on}}'}</code>
                  </p>
                </div>
                <div>
                  <Label className="text-xs">Subject</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="sign-link-subject" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">Body</Label>
                    <EmailModeToggle
                      mode={editorMode}
                      onChange={setEditorMode}
                      body={body}
                      setBody={setBody}
                      testIdPrefix="sign-link-editor-mode"
                    />
                  </div>
                  <Textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={8}
                    className={editorMode === 'html' ? 'font-mono text-xs bg-slate-900 text-emerald-300' : 'text-sm'}
                    data-testid="sign-link-body"
                  />
                </div>
              </>
            )}

            <div className="text-[11px] text-slate-500 p-2 rounded bg-slate-50 border border-slate-200">
              The system will automatically send <strong>reminder emails every 7 days</strong> (up to 4 times) until the variation is signed, using the <em>Variation — Sign Reminder</em> template.
            </div>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sign-link-close">Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              {sendEmail && (
                <Button
                  variant="outline"
                  onClick={() => setPreviewOpen(true)}
                  disabled={busy || (!subject.trim() && !body.trim())}
                  data-testid="sign-link-preview-btn"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Preview
                </Button>
              )}
              <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sign-link-send">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                {sendEmail ? 'Send Email' : 'Generate Link'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <EmailPreviewDialog
      open={previewOpen}
      onOpenChange={setPreviewOpen}
      to={customer?.email}
      subject={replaceCustomerVars(subject, { ...customer, sign_link: '(link minted on send)', expires_on: '(set on send)' })}
      bodyHtml={replaceCustomerVars(resolveBodyHtml(body, editorMode), { ...customer, sign_link: '#preview-only', expires_on: '(set on send)' })}
      onConfirmSend={submit}
      sending={busy}
      recipientLabel={customer?.first_name || 'the customer'}
    />
    </>
  );
};

export default SendSignLinkDialog;
